import React, {useState, useEffect} from 'react';

import {Icon, ToggleButton, ErrorFilledSVG} from '@newsela/angelou';
import {actionNames, objectTypes, withTrackEvent} from '@newsela/snitch';
import PropTypes from 'prop-types';

import AiSparkWithGradientIcon from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/AISearchMenuContainer/AiSparkWithGradientIcon/AiSparkWithGradientIcon.jsx';

import {
  innerContentCss,
  innerBorderCss,
  rootContainerCss,
  svgCss,
  textAreaRootContainerCss,
  labelTextCss,
  textAreaCss,
  headingSectionExpandedCss,
  toggleSwitchContainerCss,
  footerSectionCss,
  charLimitContainerCss,
  charLimitIconCss,
  charLimitInfoCss,
  charLimitCss,
  charLimitSlotCss,
} from './AIExpandableTextArea.styles';

const MAX_CHARS = 400;

const ToggleWithTrackEvent = withTrackEvent(ToggleButton);

const textAreaDescriptor = 'search-with-luna-textarea';

const searchInputExamples = [
  '3rd grade articles about the concept of community helpers',
  'Videos about how photosynthesis works',
  'Text sets explaining figurative language',
];

/**
 * Returns a random integer between 0 (inclusive) and `max` (exclusive).
 */
const getRandomInteger = (max) => Math.floor(Math.random() * max);

const AIExpandableTextArea = ({
  containerRef,
  expanded,
  handleExpandAndFocus,
  handleContainerBlur,
  isMobileDisplay,
  inputValue,
  setInputValue,
  textAreaRef,
  pressed,
  onToggle,
  onChange,
  onKeyDown,
}) => {
  const isCharacterLimitReached = inputValue.length === MAX_CHARS;

  const sparkleIcon = (
    <Icon
      SvgComponent={AiSparkWithGradientIcon}
      size={Icon.sizes.sm}
      isDecorative
      __cssFor={{root: svgCss}}
    />
  );

  // This is a simple state to hold the index of the search examples
  // array. When the text area is expanded, we set a new value for the index
  // and render one of the strings of `searchInputExamples`.
  const [searchExampleIndex, setSearchExampleIndex] = useState(
    getRandomInteger(searchInputExamples.length),
  );
  // When the text area is expanded, we cycle through the list of search
  // examples.
  useEffect(() => {
    if (expanded === true && inputValue.length === 0) {
      setSearchExampleIndex(
        (searchExampleIndex + 1) % searchInputExamples.length,
      );
    }
  }, [expanded]);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      aria-expanded={expanded}
      aria-label="Search with Luna input"
      onFocus={handleExpandAndFocus}
      onBlur={handleContainerBlur}
      className={rootContainerCss(expanded, isMobileDisplay)}
      data-qa-selector="search-with-luna-toggle"
    >
      <div className={innerBorderCss(expanded, isMobileDisplay)}>
        <div className={innerContentCss(expanded)}>
          {expanded ? (
            <div className={headingSectionExpandedCss}>
              {sparkleIcon}
              <span>What are you looking for?</span>
            </div>
          ) : (
            sparkleIcon
          )}
          <div
            className={textAreaRootContainerCss(expanded, isCharacterLimitReached)}
          >
            <label htmlFor={textAreaDescriptor} className={labelTextCss}>
              Search with Luna
            </label>
            <textarea
              className={textAreaCss(expanded)}
              ref={textAreaRef}
              id={textAreaDescriptor}
              value={inputValue}
              maxLength={MAX_CHARS}
              placeholder={
                expanded
                  ? `For example: “${searchInputExamples[searchExampleIndex]}”`
                  : 'What are you looking for?'
              }
              onChange={onChange}
              onKeyDown={onKeyDown}
              data-qa-selector={textAreaDescriptor}
              name="needle"
              rows={expanded ? 7 : 1}
            />
          </div>
          {expanded ? (
            <div
              className={charLimitSlotCss(isCharacterLimitReached)}
              aria-live="polite"
            >
              <div className={charLimitContainerCss}>
                <Icon SvgComponent={ErrorFilledSVG} __cssFor={{root: charLimitIconCss}} />
                <div className={charLimitInfoCss}>
                  Character limit reached. Please shorten your text to search.
                </div>
              </div>
            </div>
          ) : null}
          {expanded ? (
            <div className={footerSectionCss}>
              <ToggleWithTrackEvent
                label="Disable Search with Luna"
                __cssFor={{
                  switchContainerStyles: pressed
                    ? toggleSwitchContainerCss
                    : '',
                }}
                dataProps={{
                  'data-qa-selector': 'disable-search-with-luna-toggle',
                }}
                objectType={objectTypes.user}
                actionName={actionNames.interact}
                streamProperties={{
                  description: 'Toggle Search with Luna',
                }}
                pressed={pressed}
                onClick={onToggle}
              />
              <div className={charLimitCss(isCharacterLimitReached)}>
                {inputValue.length}/{MAX_CHARS} characters
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

AIExpandableTextArea.propTypes = {
  containerRef: PropTypes.object,
  expanded: PropTypes.bool,
  handleExpandAndFocus: PropTypes.func,
  handleContainerBlur: PropTypes.func,
  isMobileDisplay: PropTypes.bool,
  inputValue: PropTypes.string,
  setExpanded: PropTypes.func,
  setInputValue: PropTypes.func,
  textAreaRef: PropTypes.object,
  pressed: PropTypes.bool,
  onToggle: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
};
export default AIExpandableTextArea;
